import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search as SearchIcon,
  LocalHospital as HospitalIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  GetApp as ExportIcon
} from '@material-ui/icons';
import * as XLSX from 'xlsx';
import { trackPromise } from 'react-promise-tracker';
import Alert from '../../../components/Alert';
import ConsultaPlazasService from '../../../services/ConsultaPlazasService';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  searchBox: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  searchField: {
    flex: 1,
  },
  exportButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  servicioCell: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  contactInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  contactIcon: {
    fontSize: 16,
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  statsChip: {
    margin: theme.spacing(0.25),
  },
  nivel1: { backgroundColor: '#e3f2fd', color: '#1976d2' },
  nivel2: { backgroundColor: '#e8f5e8', color: '#388e3c' },
  nivel3: { backgroundColor: '#fff3e0', color: '#f57c00' },
  nivel4: { backgroundColor: '#fce4ec', color: '#c2185b' },
  regionChip: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
  },
}));

const ServiciosConPlazas = ({ filtrosActivos = {} }) => {
  const classes = useStyles();
  
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterData();
  }, [data, searchTerm, filtrosActivos]);

  const loadData = async () => {
    try {
      setLoading(true);
      let result = [];

      try {
        result = await trackPromise(ConsultaPlazasService.getServiciosConPlazas());
      } catch (apiError) {
        console.warn('Error en API, usando datos de ejemplo:', apiError);
        // Datos de ejemplo para mostrar la funcionalidad
        result = [
          {
            servicio_id: 1,
            servicio: 'Hospital Nacional',
            region: 'Capital',
            nivel_servicio: 'Nivel III',
            direccion: 'Av. General Santos 1234',
            telefono: '021-123456',
            email: 'contacto@hospitalnacional.gov.py',
            carreras_diferentes: 5,
            universidades_diferentes: 3,
            total_estudiantes_asignados: 85,
            plazas_confirmadas: 72
          },
          {
            servicio_id: 2,
            servicio: 'Centro de Salud San Lorenzo',
            region: 'Central',
            nivel_servicio: 'Nivel I',
            direccion: 'Calle Principal 567',
            telefono: '021-654321',
            email: 'info@csanlorenzo.gov.py',
            carreras_diferentes: 3,
            universidades_diferentes: 2,
            total_estudiantes_asignados: 28,
            plazas_confirmadas: 25
          }
        ];

        Alert.show({
          message: "Usando datos de ejemplo. Verifica que el backend esté ejecutándose.",
          type: "warning",
        });
      }

      setData(result || []);
    } catch (error) {
      console.error('Error general al cargar servicios:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = [...data];

    // Aplicar filtro de búsqueda por texto
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => (
        (item.servicio && item.servicio.toLowerCase().includes(searchLower)) ||
        (item.region && item.region.toLowerCase().includes(searchLower)) ||
        (item.nivel_servicio && item.nivel_servicio.toLowerCase().includes(searchLower)) ||
        (item.direccion && item.direccion.toLowerCase().includes(searchLower))
      ));
    }

    // Aplicar filtros avanzados
    if (filtrosActivos.region) {
      filtered = filtered.filter(item =>
        item.region && item.region.includes(filtrosActivos.region)
      );
    }

    if (filtrosActivos.nivelServicio) {
      filtered = filtered.filter(item =>
        item.nivel_servicio && item.nivel_servicio.includes(filtrosActivos.nivelServicio)
      );
    }

    console.log('Servicios filtrados:', filtered.length, 'de', data.length);
    setFilteredData(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      Alert.show({
        message: "No hay datos para exportar",
        type: "warning",
      });
      return;
    }

    const exportData = filteredData.map(item => ({
      "Servicio": item.servicio,
      "Región": item.region || 'N/A',
      "Nivel": item.nivel_servicio || 'N/A',
      "Dirección": item.direccion || 'N/A',
      "Teléfono": item.telefono || 'N/A',
      "Email": item.email || 'N/A',
      "Carreras Diferentes": item.carreras_diferentes,
      "Universidades Diferentes": item.universidades_diferentes,
      "Total Estudiantes": item.total_estudiantes_asignados,
      "Plazas Confirmadas": item.plazas_confirmadas
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Servicios");

    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `ServiciosConPlazas_${fecha}.xlsx`);

    Alert.show({
      message: "Archivo Excel exportado exitosamente",
      type: "success",
    });
  };

  const getNivelChipClass = (nivel) => {
    if (!nivel) return '';
    if (nivel.includes('I')) return classes.nivel1;
    if (nivel.includes('II')) return classes.nivel2;
    if (nivel.includes('III')) return classes.nivel3;
    if (nivel.includes('IV')) return classes.nivel4;
    return '';
  };

  const renderContactInfo = (item) => (
    <Box>
      {item.direccion && (
        <div className={classes.contactInfo}>
          <LocationIcon className={classes.contactIcon} />
          <Typography variant="body2">{item.direccion}</Typography>
        </div>
      )}
      {item.telefono && (
        <div className={classes.contactInfo}>
          <PhoneIcon className={classes.contactIcon} />
          <Typography variant="body2">{item.telefono}</Typography>
        </div>
      )}
      {item.email && (
        <div className={classes.contactInfo}>
          <EmailIcon className={classes.contactIcon} />
          <Typography variant="body2">{item.email}</Typography>
        </div>
      )}
    </Box>
  );

  const renderStatsChips = (item) => (
    <Box>
      <Tooltip title="Carreras diferentes que utilizan este servicio">
        <Chip
          icon={<SchoolIcon />}
          label={`${item.carreras_diferentes} carreras`}
          size="small"
          className={classes.statsChip}
          style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
        />
      </Tooltip>
      <Tooltip title="Universidades que envían estudiantes">
        <Chip
          icon={<BusinessIcon />}
          label={`${item.universidades_diferentes} universidades`}
          size="small"
          className={classes.statsChip}
          style={{ backgroundColor: '#e8f5e8', color: '#388e3c' }}
        />
      </Tooltip>
      <Tooltip title="Total de estudiantes asignados">
        <Chip
          label={`${item.total_estudiantes_asignados} estudiantes`}
          size="small"
          className={classes.statsChip}
          style={{ backgroundColor: '#fff3e0', color: '#f57c00' }}
        />
      </Tooltip>
      <Tooltip title="Plazas confirmadas por ambas partes">
        <Chip
          label={`${item.plazas_confirmadas} confirmadas`}
          size="small"
          className={classes.statsChip}
          style={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
        />
      </Tooltip>
    </Box>
  );

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Box className={classes.searchBox}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            placeholder="Buscar por nombre del servicio, región, nivel o dirección..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Exportar a Excel">
            <Button
              variant="contained"
              className={classes.exportButton}
              startIcon={<ExportIcon />}
              onClick={exportToExcel}
              disabled={filteredData.length === 0}
            >
              Exportar
            </Button>
          </Tooltip>
        </Box>
        
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Servicio de Salud</TableCell>
                <TableCell>Región / Nivel</TableCell>
                <TableCell>Información de Contacto</TableCell>
                <TableCell>Estadísticas de Uso</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box>
                        <Typography className={classes.servicioCell}>
                          <HospitalIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                          {item.servicio}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        {item.region && (
                          <Chip
                            label={item.region}
                            size="small"
                            className={`${classes.chip} ${classes.regionChip}`}
                          />
                        )}
                        {item.nivel_servicio && (
                          <Chip
                            label={item.nivel_servicio}
                            size="small"
                            className={`${classes.chip} ${getNivelChipClass(item.nivel_servicio)}`}
                          />
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      {renderContactInfo(item)}
                    </TableCell>
                    
                    <TableCell>
                      {renderStatsChips(item)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
      
      {filteredData.length === 0 && !loading && (
        <Paper style={{ padding: 20, textAlign: 'center', marginTop: 20 }}>
          <Typography variant="h6" color="textSecondary">
            No se encontraron servicios con plazas asignadas
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {searchTerm ? 'Intenta con otros términos de búsqueda' : 'No hay datos disponibles'}
          </Typography>
        </Paper>
      )}
    </div>
  );
};

export default ServiciosConPlazas;
